import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CustomerLayout from "../CustomerLayout";
import { API } from "../../../config";

export default function ReferralProgram() {
  const [referralCode, setReferralCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");

  const token = localStorage.getItem("token");

  const fallbackCode = useMemo(() => {
    const storedUserId =
      localStorage.getItem("customer_id") ||
      localStorage.getItem("user_id") ||
      localStorage.getItem("customerType") ||
      "early";

    return String(storedUserId).replace(/\s+/g, "").toLowerCase();
  }, []);

  const referralLink = useMemo(() => {
    const code = referralCode || fallbackCode;
    return `${window.location.origin}/ref/${code}`;
  }, [referralCode, fallbackCode]);

  useEffect(() => {
    const fetchReferral = async () => {
      try {
        const response = await fetch(`${API}/api/customer/referral`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) return;

        const data = await response.json();

        if (data?.referralCode) {
          setReferralCode(data.referralCode);
          localStorage.setItem("customerReferralCode", data.referralCode);
          return;
        }

        if (data?.code) {
          setReferralCode(data.code);
          localStorage.setItem("customerReferralCode", data.code);
        }
      } catch (err) {
        const saved = localStorage.getItem("customerReferralCode");
        if (saved) setReferralCode(saved);
      }
    };

    fetchReferral();
  }, [token]);

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setMessage("Referral link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setMessage("Copy failed. Please copy the link manually.");
    }
  };

  return (
    <CustomerLayout>
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            mb: 1,
          }}
        >
          Referrals
        </Typography>

        <Typography
          color="text.secondary"
          sx={{
            maxWidth: 760,
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          Invite businesses, shippers, drivers, or carriers to join HaggleFreight during the early access period.
        </Typography>

        {message && (
          <Alert severity={copied ? "success" : "info"} sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            mb: 3,
          }}
        >
          <Chip
            label="Invite Link"
            size="small"
            sx={{
              mb: 2,
              fontWeight: 700,
            }}
          />

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Share Your Referral Link
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
            Send your referral link to people or businesses interested in joining HaggleFreight early.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              fullWidth
              value={referralLink}
              InputProps={{
                readOnly: true,
              }}
            />

            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={copyReferralLink}
              sx={{
                minHeight: 56,
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 800,
                whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Copy Link
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                p: 2.5,
                borderRadius: 3,
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Help Build the Network
              </Typography>
              <Typography color="text.secondary">
                HaggleFreight becomes more useful as more customers, drivers, and carriers join the platform.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                p: 2.5,
                borderRadius: 3,
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Priority Onboarding
              </Typography>
              <Typography color="text.secondary">
                Approved early referrals may receive priority consideration during private beta onboarding.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary">
          Referral benefits are subject to approval, availability, and HaggleFreight’s private beta rules.
        </Typography>
      </Box>
    </CustomerLayout>
  );
}